/**
 * Manual Assessment (Triage) Form
 * Form is fully driven by GET /manual-assessment/workflows/{workflow_id}/tasks/{task_id}.
 * Renders fields and initial values from task.propertyValueItemList via FormRenderer.
 */

import React, { useState, useMemo, useCallback, useRef, useContext, useEffect } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import GlobalStyles from "@mui/material/GlobalStyles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import { ThemeProvider, alpha, createTheme, useTheme } from "@mui/material/styles";
import { FormRenderer, validateFormData, type FormData } from "../FormRenderer";
import {
  DynamicFormCompBasicJsonSchema as CommonDynamicFormCompBasicJsonSchema,
  FreyrLibraryProvider as CommonFreyrLibraryProvider,
} from "@freyr/freyr-react-common";
import { SnackbarProvider as NotistackProvider } from "notistack";
import { I18nextProvider } from "react-i18next";
import { resolveESignActionForSubmit } from "../../utils/manualAssessmentESign";
import type { ResolvedESignAction } from "../../types/eSignPolicy";
import {
  ESIGN_DIALOG_TITLE_SUFFIX,
  ESIGN_LANGUAGE,
  ESIGN_MODULE_NAME,
} from "../../constants/eSign";
import "./esignDialogStyles";
import { esignI18n } from "../../i18n/esignI18n";
import { getApiBaseUrl, getHostApiBaseUrl } from "../../config/env";
import {
  getAuthorizationHeader,
  getDomain,
  getTenantId,
  getUserId,
} from "../../services/apiClient";
import {
  taskDetailToFormConfig,
  type OptionsByField,
} from "../../utils/taskDetailToFormConfig";
import {
  resolveTaskOwnersGroupedInitial,
  taskDetailToDynamicFormConfig,
  taskDetailToPreviousTaskReadonlyFormConfig,
  taskDetailToSourceFieldsReadonlySections,
  buildDynamicFormExtraState,
  buildPreviousTaskDynamicFormExtraState,
  resolvePreviousTaskDialogTitle,
} from "../../utils/taskDetailToDynamicFormConfig";
import {
  buildAssignNextTaskFormExtraState,
  buildAssignNextTaskSubmitValue,
  hasAssignNextTaskConfig,
  parseAssignNextTaskFromTaskDetail,
  taskDetailToAssignNextTaskDynamicFormConfig,
  validateAssignNextTaskFormData,
} from "../../utils/assignNextTaskFormConfig";
import { useDebounce } from "../../hooks/useDebounce";
import { FreyrLibraryContext } from "../../context/FreyrLibraryContext";
import { getAssessmentReportPresignedUrl } from "../../services/assessmentService";
import type {
  WorkflowTaskDetail,
  WorkflowTaskReference,
} from "../../services/regulationsService";
import { reassignWorkflowTask } from "../../services/regulationsService";
import {
  coerceReferencesArray,
  normalizeWorkflowTaskDetailPayload,
  resolvePreviousTaskFromTaskDetail,
} from "../../utils/workflowTaskDetailNormalize";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/en";
import {
  SHOW_DYNAMIC_FORM,
  SHOW_LEGACY_FORM,
} from "./manualAssessmentFormFlags";
import {
  MANUAL_ASSESSMENT_ACTION_LABELS,
  formatActionButtonLabel,
} from "./manualAssessmentActionLabels";
import { openPdfUrlInNewTabForPreview } from "../../utils/openPdfInNewTab";
import ManualAssessmentTaskTable from "./ManualAssessmentTaskTable";
import TaskFormSummaryBar from "./TaskFormSummaryBar";
import ManualAssessmentReassignDialog, {
  isTaskReassignable,
} from "./ManualAssessmentReassignDialog";

type ESignModule = Pick<
  typeof import("@freyr/freyr-react-common-components"),
  "ESignValidation" | "FreyrComponentLibraryProvider"
>;
import type { ESignCallbackPayload } from "@freyr/freyr-react-common-components";
import type { TaskTableDefinition, TaskTableFormState } from "../../types/manualAssessmentWorkflow";
import {
  buildInitialTaskTableState,
  parseTaskTablesFromTaskDetail,
  validateTriageTaskTables,
  type TaskTableCellValidationError,
} from "../../utils/manualAssessmentTaskTable";
import { formatTaskDueDate } from "../../utils/taskTableHeaders";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import { isPreviousFormsRelatedMaterial } from "../../utils/wholeWorkflowIframe";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import {
  ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
  ELEVATED_DIALOG_Z_INDEX_OFFSET,
} from "../../constants/dialogZIndex";

const DEBOUNCE_MS = 300;
const ACTION_BUTTON_LOADING_ICON_SIZE = 20;

/** AC-1: Related Materials chip for `references.name === previousForms`. */
export const PREVIOUS_FORMS_LINK_LABEL = "View whole workflow";

/** AC-2: Related Materials chip that opens previous-task modal. */
export const VIEW_PREVIOUS_TASK_LINK_LABEL = "View previous task";

export type PreviousTaskDisplayMode = "inline" | "modal";

/** Resolve `data.previousTaskDisplayMode`; missing / unknown → inline. */
export function resolvePreviousTaskDisplayMode(
  taskDetail: WorkflowTaskDetail | null | undefined,
): PreviousTaskDisplayMode {
  const raw =
    taskDetail?.previousTaskDisplayMode ??
    (taskDetail as { previous_task_display_mode?: unknown } | null | undefined)
      ?.previous_task_display_mode ??
    (taskDetail?.taskPage as { previousTaskDisplayMode?: unknown } | undefined)
      ?.previousTaskDisplayMode;

  if (typeof raw === "string" && raw.trim().toLowerCase() === "modal") {
    return "modal";
  }
  return "inline";
}

dayjs.extend(customParseFormat);

/** Ref API exposed by `DynamicFormCompBasicJsonSchema` (common package). */
export type DynamicFormRef = {
  submitForm: () => void;
  getFormState: () => Record<string, unknown>;
  clearFormState: () => void;
};

type DynamicFormSubmitAction = "submit" | "approve" | "reject";

const FORM_FOOTER_SX = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 1.5,
  pt: 3,
  mt: 2,
  borderTop: "1px solid",
  borderColor: "grey.200",
  // MUI Button defaults to text-transform: uppercase; show Title Case labels as written.
  "& .MuiButton-root": {
    textTransform: "none",
  },
} as const;

/** Divider between read-only (hide) and editable dynamic form sections — matches task summary rule. */
const MANUAL_ASSESSMENT_SECTION_DIVIDER_SX = {
  pb: 2,
  borderBottom: "1px solid #F2F2F2",
  mb: 2,
} as const;

const DYNAMIC_FORM_COLUMN_GAP_PX = 30;

const DYNAMIC_FORM_DISABLED_FIELD_BORDER = "white";
const DYNAMIC_FORM_EDITABLE_FIELD_BORDER = "#D9D9D9";
/** Select dropdown arrow + date picker icon on non-editable fields */
const DYNAMIC_FORM_DISABLED_FIELD_ICON = "white";
/** Prior-step read-only block: label #8C8C8C, value #404040 */
const DYNAMIC_FORM_READONLY_LABEL_COLOR = "#8C8C8C";
const DYNAMIC_FORM_READONLY_VALUE_COLOR = "#404040";
/** Current-step editable block: label #404040; value #404040 when filled, #8C8C8C when empty */
const DYNAMIC_FORM_EDITABLE_LABEL_COLOR = "#404040";
const DYNAMIC_FORM_FILLED_VALUE_COLOR = "#404040";
const DYNAMIC_FORM_EMPTY_VALUE_COLOR = "#8C8C8C";

const DYNAMIC_FORM_FIELD_TEXT_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: 14,
  lineHeight: "20px",
  fontWeight: 400,
} as const;

const DYNAMIC_FORM_LAYOUT_SX = {
  "& .MuiGrid-container": {
    rowGap: "12px",
    width: `calc(100% + ${DYNAMIC_FORM_COLUMN_GAP_PX}px)`,
    marginLeft: `-${DYNAMIC_FORM_COLUMN_GAP_PX / 2}px`,
    marginRight: `-${DYNAMIC_FORM_COLUMN_GAP_PX / 2}px`,
  },
  "& .MuiGrid-item": {
    paddingLeft: `${DYNAMIC_FORM_COLUMN_GAP_PX / 2}px`,
    paddingRight: `${DYNAMIC_FORM_COLUMN_GAP_PX / 2}px`,
    paddingBottom: "4px",
  },
  "& .MuiGrid-item:last-of-type .form-group": {
    marginBottom: 0,
  },
  // Value / placeholder typography — match label (14px)
  "& .MuiInputBase-input, & .MuiInputBase-inputMultiline, & .MuiSelect-select, & .MuiAutocomplete-input, & .MuiChip-label":
    DYNAMIC_FORM_FIELD_TEXT_SX,
  "& .MuiInputBase-input::placeholder, & .MuiInputBase-inputMultiline::placeholder":
    {
      ...DYNAMIC_FORM_FIELD_TEXT_SX,
      opacity: 1,
    },
  "& .MuiInputBase-root:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
    borderColor: DYNAMIC_FORM_EDITABLE_FIELD_BORDER,
  },
  "& .MuiInputBase-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline":
    {
      borderColor: DYNAMIC_FORM_EDITABLE_FIELD_BORDER,
    },
  "& .MuiInputBase-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: DYNAMIC_FORM_DISABLED_FIELD_BORDER,
  },
  "& .MuiInputBase-root.Mui-disabled:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: DYNAMIC_FORM_DISABLED_FIELD_BORDER,
  },
  // Autocomplete select — disabled popup arrow (triangle)
  "& .MuiInputBase-root.Mui-disabled .MuiAutocomplete-endAdornment .MuiSvgIcon-root":
    {
      color: `${DYNAMIC_FORM_DISABLED_FIELD_ICON} !important`,
    },
  "& .MuiInputBase-root.Mui-disabled .MuiAutocomplete-popupIndicator": {
    color: `${DYNAMIC_FORM_DISABLED_FIELD_ICON} !important`,
  },
  "& .MuiInputBase-root.Mui-disabled .MuiAutocomplete-popupIndicator.Mui-disabled":
    {
      color: `${DYNAMIC_FORM_DISABLED_FIELD_ICON} !important`,
    },
  // Date picker — disabled calendar icon (and clear icon when shown)
  "& .MuiInputBase-root.Mui-disabled .MuiInputAdornment-root .MuiSvgIcon-root": {
    color: `${DYNAMIC_FORM_DISABLED_FIELD_ICON} !important`,
  },
  "& .MuiInputBase-root.Mui-disabled .MuiInputAdornment-root .MuiIconButton-root.Mui-disabled":
    {
      color: `${DYNAMIC_FORM_DISABLED_FIELD_ICON} !important`,
      opacity: 1,
    },
} as const;

function buildDynamicFormFieldColorSx(
  labelColor: string,
  valueColor: string,
  options?: { distinguishEmptyValue?: boolean },
): Record<string, unknown> {
  const sx: Record<string, unknown> = {
    // CustomizedFormLabel + CustomViewWidget labels
    "& .MuiFormLabel-root, & .MuiFormLabel-root.Mui-disabled": {
      color: `${labelColor} !important`,
      ...DYNAMIC_FORM_FIELD_TEXT_SX,
    },
    // CustomViewWidget (viewType): value text below label.
    // Preserve author newlines in comments (HTML collapses whitespace by default).
    "& .MuiFormLabel-root + .MuiBox-root, & .MuiFormLabel-root + span .MuiBox-root":
      {
        color: valueColor,
        ...DYNAMIC_FORM_FIELD_TEXT_SX,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      },
    // Disabled field values (input / textarea / chip)
    "& .MuiInputBase-root.Mui-disabled .MuiInputBase-input": {
      ...DYNAMIC_FORM_FIELD_TEXT_SX,
      color: `${valueColor} !important`,
      WebkitTextFillColor: `${valueColor} !important`,
      opacity: 1,
    },
    "& .MuiInputBase-root.Mui-disabled .MuiInputBase-inputMultiline": {
      ...DYNAMIC_FORM_FIELD_TEXT_SX,
      color: `${valueColor} !important`,
      WebkitTextFillColor: `${valueColor} !important`,
      opacity: 1,
    },
    "& .MuiInputBase-root.Mui-disabled .MuiChip-label": {
      ...DYNAMIC_FORM_FIELD_TEXT_SX,
      color: valueColor,
    },
  };

  if (options?.distinguishEmptyValue) {
    Object.assign(sx, {
      // Empty editable fields default to lighter text / placeholder
      "& .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-input, & .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-inputMultiline, & .MuiInputBase-root:not(.Mui-disabled) .MuiSelect-select":
        {
          ...DYNAMIC_FORM_FIELD_TEXT_SX,
          color: DYNAMIC_FORM_EMPTY_VALUE_COLOR,
        },
      "& .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-input::placeholder, & .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-inputMultiline::placeholder":
        {
          ...DYNAMIC_FORM_FIELD_TEXT_SX,
          color: DYNAMIC_FORM_EMPTY_VALUE_COLOR,
          opacity: 1,
        },
      // Text inputs with content
      "& .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-input:not(:placeholder-shown), & .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-inputMultiline:not(:placeholder-shown)":
        {
          ...DYNAMIC_FORM_FIELD_TEXT_SX,
          color: DYNAMIC_FORM_FILLED_VALUE_COLOR,
        },
      // Select / autocomplete with a chosen value
      "& .MuiInputBase-root:not(.Mui-disabled):has(.MuiSelect-nativeInput:not([value=''])) .MuiSelect-select, & .MuiInputBase-root:not(.Mui-disabled):has(.MuiAutocomplete-input:not([value=''])) .MuiAutocomplete-input":
        {
          ...DYNAMIC_FORM_FIELD_TEXT_SX,
          color: DYNAMIC_FORM_FILLED_VALUE_COLOR,
        },
    });
  } else {
    Object.assign(sx, {
      "& .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-input, & .MuiInputBase-root:not(.Mui-disabled) .MuiInputBase-inputMultiline":
        {
          ...DYNAMIC_FORM_FIELD_TEXT_SX,
          color: valueColor,
        },
    });
  }

  return sx;
}

const DYNAMIC_FORM_READONLY_SECTION_SX = {
  ...DYNAMIC_FORM_LAYOUT_SX,
  ...buildDynamicFormFieldColorSx(
    DYNAMIC_FORM_READONLY_LABEL_COLOR,
    DYNAMIC_FORM_READONLY_VALUE_COLOR,
  ),
} as const;

/**
 * sourceFields values wrap in full (no ellipsis). CustomViewWidget still wraps
 * every value in Tooltip; disable pointer events so hover is not shown.
 */
const SOURCE_FIELDS_READONLY_SECTION_SX = {
  ...DYNAMIC_FORM_READONLY_SECTION_SX,
  "& .text-ellipsis": {
    overflow: "visible",
    textOverflow: "unset",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
  "& *": {
    pointerEvents: "none",
  },
} as const;

/**
 * Previous-task dialog body: show full field text (no ellipsis). Freyr
 * CustomViewWidget always wraps values in Tooltip; hover tips are redundant
 * when content is not truncated (see PREVIOUS_TASK_HIDE_CONTENT_TOOLTIPS_SX).
 */
const PREVIOUS_TASK_DIALOG_CONTENT_FORM_SX = {
  ...DYNAMIC_FORM_READONLY_SECTION_SX,
  "& .text-ellipsis": {
    overflow: "visible",
    textOverflow: "unset",
    whiteSpace: "normal",
    wordBreak: "break-word",
  },
} as const;

/**
 * Freyr ships a nested `@mui/material`, so app ThemeProvider defaultProps never
 * reach CustomViewWidget tooltips (they portal to `body`). Hide those poppers
 * while the dialog is open; whitelist Available Owners via className.
 */
const PREVIOUS_TASK_META_TOOLTIP_POPPER_CLASS = "previous-task-meta-tooltip";

const PREVIOUS_TASK_HIDE_CONTENT_TOOLTIPS_SX = {
  [`body .MuiTooltip-popper:not(.${PREVIOUS_TASK_META_TOOLTIP_POPPER_CLASS})`]: {
    display: "none !important",
  },
} as const;

const DYNAMIC_FORM_EDITABLE_SECTION_SX = {
  ...DYNAMIC_FORM_LAYOUT_SX,
  ...buildDynamicFormFieldColorSx(
    DYNAMIC_FORM_EDITABLE_LABEL_COLOR,
    DYNAMIC_FORM_FILLED_VALUE_COLOR,
    { distinguishEmptyValue: true },
  ),
} as const;

/**
 * `@freyr/freyr-react-common` BaseInputTemplate calls preventDefault on every Enter,
 * including multiline textareas. Capture on the form wrapper and stop propagation so
 * textarea keeps native newline behavior (paste already worked).
 */
export function allowTextareaEnterNewlineCapture(
  e: React.KeyboardEvent<HTMLElement>,
): void {
  if (e.key !== "Enter") return;
  if (!(e.target instanceof HTMLTextAreaElement)) return;
  e.stopPropagation();
}

function mergeResolvedTaskOwnersInitial(
  base: Record<string, unknown>,
  resolved: Record<string, string[]> | null,
): Record<string, unknown> {
  if (!resolved || base.taskOwners === undefined) return base;
  return { ...base, taskOwners: resolved };
}

const PRIMARY_ACTION_BUTTON_SX = {
  textTransform: "none",
} as const;

/** Figma node 6834-45916 — previous-task modal surface. */
const PREVIOUS_TASK_DIALOG_PAPER_SX = {
  width: 808,
  maxWidth: "calc(100vw - 32px)",
  borderRadius: "8px",
  boxSizing: "border-box" as const,
};

const PREVIOUS_TASK_DIALOG_TITLE_SX = {
  m: 0,
  px: 3,
  pt: 2.5,
  pb: 1.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 700,
  fontSize: 18,
  lineHeight: "28px",
  color: "#172B4D",
} as const;

const PREVIOUS_TASK_META_LABEL_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  color: "#8C8C8C",
} as const;

const PREVIOUS_TASK_META_VALUE_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  color: "#262626",
} as const;

const PREVIOUS_TASK_OK_BUTTON_SX = {
  textTransform: "none" as const,
  fontFamily: '"Nunito Sans", sans-serif',
  fontSize: 14,
  fontWeight: 600,
  minWidth: 64,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

function PreviousTaskDialogMetaField({
  label,
  value,
  tooltip,
}: Readonly<{
  label: string;
  value: string;
  tooltip?: string | null;
}>) {
  const content = (
    <Typography component="span" sx={PREVIOUS_TASK_META_LABEL_SX}>
      {label}:{" "}
      <Box component="span" sx={PREVIOUS_TASK_META_VALUE_SX}>
        {value}
      </Box>
    </Typography>
  );

  if (!tooltip?.trim()) return content;

  return (
    <Tooltip
      title={tooltip}
      arrow
      slotProps={{
        popper: {
          className: PREVIOUS_TASK_META_TOOLTIP_POPPER_CLASS,
          sx: {
            zIndex: (theme) =>
              theme.zIndex.modal +
              ELEVATED_DIALOG_Z_INDEX_OFFSET +
              ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
          },
        },
        tooltip: {
          sx: { maxWidth: 560, whiteSpace: "normal" },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "baseline",
          flexWrap: "wrap",
          ...PREVIOUS_TASK_META_LABEL_SX,
          cursor: "help",
        }}
      >
        {label}:{" "}
        <Box component="span" sx={PREVIOUS_TASK_META_VALUE_SX}>
          {value}
        </Box>
      </Box>
    </Tooltip>
  );
}

type TaskPersonLike = {
  firstName?: string | null;
  lastName?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
};

/** "FirstName LastName" from workflow task person (camelCase or snake_case). */
export function formatWorkflowTaskPersonName(
  person?: TaskPersonLike | null,
): string | null {
  if (!person || typeof person !== "object") return null;
  const parts = [person.firstName ?? person.first_name, person.lastName ?? person.last_name]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  const fallback = person.name?.trim();
  return fallback || null;
}

/** Comma-separated full names from `task.reviewers` for Available Owners tooltip. */
export function formatAvailableOwnerNames(reviewers: unknown): string | null {
  if (!Array.isArray(reviewers) || reviewers.length === 0) return null;
  const names = reviewers
    .map((entry) => formatWorkflowTaskPersonName(entry as TaskPersonLike))
    .filter((name): name is string => Boolean(name?.trim()));
  return names.length > 0 ? names.join(", ") : null;
}

function formatPersonName(person?: TaskPersonLike | null): string | null {
  return formatWorkflowTaskPersonName(person);
}

export { formatTaskDueDate } from "../../utils/taskTableHeaders";

function isPreviousAssessmentTask(
  previousTask: WorkflowTaskDetail["previousTask"],
): boolean {
  if (!previousTask) return false;
  return (
    (previousTask.taskInstanceName ?? "").trim().toLowerCase() === "assessment"
  );
}

export type TaskFormSummary = NonNullable<ReturnType<typeof getTaskFormSummary>>;

export type PreviousTaskDialogMeta = {
  availableOwnerCount: number;
  availableOwnerNames: string | null;
  assigneeName: string | null;
  taskStartDate: string | null;
  taskEndDate: string | null;
};

/** Owners / assignee / start-end dates strip for the previous-task modal (Figma 6834-45916). */
export function getPreviousTaskDialogMeta(
  taskDetail: WorkflowTaskDetail | null | undefined,
): PreviousTaskDialogMeta | null {
  const previousTask = resolvePreviousTaskFromTaskDetail(taskDetail);
  if (!previousTask) return null;

  const reviewers = previousTask.reviewers;
  const availableOwnerCount = Array.isArray(reviewers) ? reviewers.length : 0;
  const availableOwnerNames = formatAvailableOwnerNames(reviewers);
  const assigneeName = formatPersonName(previousTask.taskAssignee);
  const taskStartDate = formatTaskDueDate(previousTask.actualStartDate);
  const taskEndDate = formatTaskDueDate(previousTask.actualEndDate);

  if (
    availableOwnerCount <= 0 &&
    !assigneeName &&
    !taskStartDate &&
    !taskEndDate
  ) {
    return null;
  }

  return {
    availableOwnerCount,
    availableOwnerNames,
    assigneeName,
    taskStartDate,
    taskEndDate,
  };
}

export function getTaskFormSummary(taskDetail: WorkflowTaskDetail | null) {
  const task = taskDetail?.task as
    | {
        reviewers?: unknown;
        taskAssignee?: TaskPersonLike;
        dueDate?: unknown;
      }
    | undefined;

  if (!task) return null;

  const reviewers = task.reviewers;
  const availableOwnerCount = Array.isArray(reviewers) ? reviewers.length : 0;
  const availableOwnerNames = formatAvailableOwnerNames(reviewers);
  const currentAssigneeName = formatPersonName(task.taskAssignee);
  const previousTaskResolved = resolvePreviousTaskFromTaskDetail(taskDetail);
  const assessorName = formatPersonName(previousTaskResolved?.taskAssignee);
  const dueDate = formatTaskDueDate(task.dueDate);
  const showDateOfImpactAssessment = isPreviousAssessmentTask(
    previousTaskResolved,
  );
  const previousTask = previousTaskResolved as
    | { actualStartDate?: unknown }
    | undefined;
  const dateOfImpactAssessment = showDateOfImpactAssessment
    ? formatTaskDueDate(previousTask?.actualStartDate)
    : null;

  return {
    availableOwnerCount,
    availableOwnerNames,
    currentAssigneeName,
    assessorName,
    dueDate,
    showDateOfImpactAssessment,
    dateOfImpactAssessment,
  };
}

export type TaskReferenceTextField = {
  field: string;
  label: string;
  value: string;
  order?: number;
};

/** Ordered Related Materials entry (link chip or read-only text). */
export type RelatedMaterialDisplayItem =
  | { kind: "link"; order: number; material: WorkflowTaskReference }
  | { kind: "text"; order: number; field: TaskReferenceTextField };

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value == null || typeof value !== "object") return undefined;
  return value as Record<string, unknown>;
}

const MONGO_OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

function resolveReferenceDisplayValue(
  item: WorkflowTaskReference,
  taskDetail: WorkflowTaskDetail | null,
): string {
  const rawValue = item.value;
  if (rawValue == null || String(rawValue).trim() === "") return "";

  const raw = String(rawValue).trim();
  if (item.field === "originalTitle" && MONGO_OBJECT_ID_PATTERN.test(raw)) {
    const details = asRecord(asRecord(taskDetail?.associatedEntity)?.details);
    const resolved =
      details?.title ?? details?.originalTitle ?? details?.original_title;
    if (typeof resolved === "string" && resolved.trim()) {
      return resolved.trim();
    }
  }

  return raw;
}

function mergeReferenceItems(
  merged: WorkflowTaskReference[],
  seen: Set<string>,
  candidate: unknown,
): void {
  for (const item of coerceReferencesArray(candidate)) {
    const dedupeKey = [
      item.field ?? "",
      item.uri ?? "",
      item.value ?? "",
      item.name ?? "",
    ].join("|");
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    merged.push(item);
  }
}

/** Ascending `references[].order`; missing/invalid order sorts last. */
export function getTaskReferenceOrder(
  item: Pick<WorkflowTaskReference, "order"> | null | undefined,
): number {
  const order = item?.order;
  return typeof order === "number" && Number.isFinite(order)
    ? order
    : Number.POSITIVE_INFINITY;
}

export function sortTaskReferencesByOrder(
  references: readonly WorkflowTaskReference[],
): WorkflowTaskReference[] {
  return [...references].sort(
    (left, right) => getTaskReferenceOrder(left) - getTaskReferenceOrder(right),
  );
}

function collectReferencesDeep(
  obj: unknown,
  merged: WorkflowTaskReference[],
  seen: Set<string>,
  maxDepth = 6,
  depth = 0,
): void {
  if (depth >= maxDepth || obj == null || typeof obj !== "object" || Array.isArray(obj)) {
    return;
  }

  const record = obj as Record<string, unknown>;
  mergeReferenceItems(merged, seen, record.references);

  for (const value of Object.values(record)) {
    if (value != null && typeof value === "object" && !Array.isArray(value)) {
      collectReferencesDeep(value, merged, seen, maxDepth, depth + 1);
    }
  }
}

/**
 * Collect `references` from GET workflow task response.
 * Backend may attach them on `task`, response root, `previousTask`, `taskPage`,
 * `associatedEntity`, or nested variants of those objects.
 */
export function parseTaskReferencesFromTaskDetail(
  taskDetail: WorkflowTaskDetail | null | undefined,
): WorkflowTaskReference[] {
  const normalized = taskDetail
    ? normalizeWorkflowTaskDetailPayload(taskDetail)
    : null;
  if (!normalized) return [];

  const root = asRecord(normalized);
  const task = asRecord(normalized.task);
  const previousTask = asRecord(normalized.previousTask);
  const taskPage = asRecord(normalized.taskPage);
  const associatedEntity = asRecord(root?.associatedEntity);
  const associatedDetails = asRecord(associatedEntity?.details);

  const candidates: unknown[] = [
    task?.references,
    root?.references,
    previousTask?.references,
    taskPage?.references,
    asRecord(task?.taskPage)?.references,
    asRecord(previousTask?.task)?.references,
    associatedEntity?.references,
    associatedDetails?.references,
  ];

  const merged: WorkflowTaskReference[] = [];
  const seen = new Set<string>();

  for (const value of candidates) {
    mergeReferenceItems(merged, seen, value);
  }

  if (merged.length === 0) {
    collectReferencesDeep(normalized, merged, seen);
  }

  return sortTaskReferencesByOrder(merged);
}

/** Related material links from task references (S3 uri → presigned URL; previousForms → side pane; other http(s) → new tab). */
export function getTaskRelatedMaterials(
  taskDetail: WorkflowTaskDetail | null,
): WorkflowTaskReference[] {
  return parseTaskReferencesFromTaskDetail(taskDetail).filter(
    (item) =>
      typeof item.uri === "string" &&
      item.uri.trim() !== "",
  );
}

export function isRelatedMaterialS3Uri(uri: string): boolean {
  return uri.trim().toLowerCase().startsWith("s3://");
}

export function isRelatedMaterialExternalUri(uri: string): boolean {
  const trimmed = uri.trim().toLowerCase();
  return trimmed.startsWith("http://") || trimmed.startsWith("https://");
}

export function getRelatedMaterialLabel(material: WorkflowTaskReference): string {
  const name = typeof material.name === "string" ? material.name.trim() : "";
  if (name === "previousForms") {
    return PREVIOUS_FORMS_LINK_LABEL;
  }

  const label =
    typeof material.label === "string" ? material.label.trim() : "";
  if (label) return label;

  if (name) return name;

  return "Document";
}

/** Read-only reference fields from task references (e.g. originalTitle text/plain). */
export function getTaskReferenceTextFields(
  taskDetail: WorkflowTaskDetail | null,
): TaskReferenceTextField[] {
  return getTaskRelatedMaterialDisplayItems(taskDetail)
    .filter(
      (item): item is Extract<RelatedMaterialDisplayItem, { kind: "text" }> =>
        item.kind === "text",
    )
    .map((item) => item.field);
}

/**
 * Related Materials entries sorted by `references[].order`
 * (links and text fields interleaved).
 */
export function getTaskRelatedMaterialDisplayItems(
  taskDetail: WorkflowTaskDetail | null,
): RelatedMaterialDisplayItem[] {
  const items: RelatedMaterialDisplayItem[] = [];

  for (const item of parseTaskReferencesFromTaskDetail(taskDetail)) {
    const order = getTaskReferenceOrder(item);
    const uri = typeof item.uri === "string" ? item.uri.trim() : "";
    if (uri) {
      items.push({ kind: "link", order, material: item });
      continue;
    }

    const rawValue = item.value;
    if (rawValue == null || String(rawValue).trim() === "") continue;

    const field =
      typeof item.field === "string" && item.field.trim()
        ? item.field.trim()
        : "";
    const label =
      (typeof item.name === "string" && item.name.trim()
        ? item.name.trim()
        : "") ||
      field ||
      "Reference";
    items.push({
      kind: "text",
      order,
      field: {
        field: field || label,
        label,
        value: resolveReferenceDisplayValue(item, taskDetail),
        ...(Number.isFinite(order) ? { order } : {}),
      },
    });
  }

  return items;
}

const RELATED_MATERIALS_LABEL_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 600,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#262626",
} as const;

/** Figma 6834-45916 — Related Materials link chips (View whole workflow / View previous task). */
const RELATED_MATERIAL_CHIP_SX = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: "4px",
  boxSizing: "border-box",
  height: 26,
  py: "3px",
  px: "8px",
  borderRadius: "4px",
  bgcolor: "#EFF6FF",
  border: "none",
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#295EF1",
  cursor: "pointer",
  textDecoration: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: "#DBEAFE",
  },
  "&:disabled": {
    cursor: "wait",
    opacity: 0.7,
  },
} as const;

const RELATED_MATERIAL_TEXT_FIELD_CARD_SX = {
  boxSizing: "border-box",
  width: "100%",
  maxWidth: "100%",
  alignSelf: "stretch",
  minHeight: 66,
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  p: "12px",
  borderRadius: "4px",
  bgcolor: "#FAFAFA",
} as const;

const RELATED_MATERIAL_ORIGINAL_TITLE_LABEL_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: "16px",
  letterSpacing: 0,
  textTransform: "capitalize",
  color: "#8C8C8C",
} as const;

const RELATED_MATERIAL_ORIGINAL_TITLE_VALUE_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  verticalAlign: "middle",
  color: "#262626",
  // Preserve author newlines from triage Original Title textarea.
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
} as const;

const RELATED_MATERIAL_TEXT_LABEL_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#8C8C8C",
} as const;

const RELATED_MATERIAL_TEXT_VALUE_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#262626",
} as const;

const RELATED_MATERIAL_LINK_ICON_SX = {
  width: 14,
  height: 7,
  flexShrink: 0,
  display: "block",
} as const;

/** Link chain icon; fill follows chip text via `currentColor`. */
function RelatedMaterialLinkIcon() {
  return (
    <Box
      component="svg"
      width={14}
      height={7}
      viewBox="0 0 14 7"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      sx={RELATED_MATERIAL_LINK_ICON_SX}
    >
      <path
        d="M1.26667 3.33333C1.26667 2.19333 2.19333 1.26667 3.33333 1.26667H6V0H3.33333C1.49333 0 0 1.49333 0 3.33333C0 5.17333 1.49333 6.66667 3.33333 6.66667H6V5.4H3.33333C2.19333 5.4 1.26667 4.47333 1.26667 3.33333ZM4 4H9.33333V2.66667H4V4ZM10 0H7.33333V1.26667H10C11.14 1.26667 12.0667 2.19333 12.0667 3.33333C12.0667 4.47333 11.14 5.4 10 5.4H7.33333V6.66667H10C11.84 6.66667 13.3333 5.17333 13.3333 3.33333C13.3333 1.49333 11.84 0 10 0Z"
        fill="currentColor"
      />
    </Box>
  );
}

const TASK_STEP_BADGE_SX = {
  width: 24,
  height: 24,
  borderRadius: "50%",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 600,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  flexShrink: 0,
} as const;

const TASK_STEP_LABEL_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 600,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#262626",
} as const;

const TASK_STEP_CONNECTOR_SX = {
  width: 128,
  height: 0,
  borderTop: "1px solid #A6A6A6",
  flexShrink: 0,
} as const;

function TaskFormStepIndicator({
  stepNumber,
  label,
}: {
  stepNumber: number;
  label: string;
}) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Box
        component="span"
        sx={(theme) => ({
          ...TASK_STEP_BADGE_SX,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: theme.palette.primary.main,
        })}
      >
        {stepNumber}
      </Box>
      <Typography component="span" sx={TASK_STEP_LABEL_SX}>
        {label}
      </Typography>
    </Box>
  );
}

function isOriginalTitleReferenceField(entry: TaskReferenceTextField): boolean {
  return entry.field === "originalTitle";
}

function TaskRelatedMaterialsSection({
  displayItems,
  openingUri,
  error,
  onOpen,
  showViewPreviousTaskLink = false,
  onOpenPreviousTask,
}: {
  displayItems: RelatedMaterialDisplayItem[];
  openingUri: string | null;
  error: string | null;
  onOpen: (uri: string) => void;
  showViewPreviousTaskLink?: boolean;
  onOpenPreviousTask?: () => void;
}) {
  if (displayItems.length === 0 && !showViewPreviousTaskLink) {
    return null;
  }

  return (
    <Box sx={{ mb: 2, width: "100%" }}>
      <Typography component="div" sx={RELATED_MATERIALS_LABEL_SX}>
        Related Materials
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 0.75,
          mt: 0.75,
          width: "100%",
        }}
      >
        {showViewPreviousTaskLink ? (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={() => onOpenPreviousTask?.()}
              sx={RELATED_MATERIAL_CHIP_SX}
            >
              <RelatedMaterialLinkIcon />
              {VIEW_PREVIOUS_TASK_LINK_LABEL}
            </Box>
          </Box>
        ) : null}
        {displayItems.map((item) => {
          if (item.kind === "text") {
            const entry = item.field;
            if (isOriginalTitleReferenceField(entry)) {
              return (
                <Box
                  key={`text-${entry.field}-${entry.value}`}
                  sx={RELATED_MATERIAL_TEXT_FIELD_CARD_SX}
                >
                  <Typography
                    component="div"
                    sx={RELATED_MATERIAL_ORIGINAL_TITLE_LABEL_SX}
                  >
                    {entry.label}
                  </Typography>
                  <Typography
                    component="div"
                    sx={RELATED_MATERIAL_ORIGINAL_TITLE_VALUE_SX}
                  >
                    {entry.value}
                  </Typography>
                </Box>
              );
            }
            return (
              <Typography
                key={`text-${entry.field}-${entry.value}`}
                component="span"
                sx={RELATED_MATERIAL_TEXT_LABEL_SX}
              >
                {entry.label}:{" "}
                <Box component="span" sx={RELATED_MATERIAL_TEXT_VALUE_SX}>
                  {entry.value}
                </Box>
              </Typography>
            );
          }

          const uri = item.material.uri!.trim();
          const label = getRelatedMaterialLabel(item.material);
          const isOpening = openingUri === uri;
          return (
            <Box
              key={`link-${uri}`}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <Box
                component="button"
                type="button"
                disabled={isOpening}
                onClick={() => void onOpen(uri)}
                sx={RELATED_MATERIAL_CHIP_SX}
              >
                <RelatedMaterialLinkIcon />
                {label}
              </Box>
            </Box>
          );
        })}
      </Box>
      {error && (
        <Typography
          component="p"
          sx={{ mt: 0.75, fontSize: 12, color: "error.main" }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
}

export type ManualAssessmentFormContext = {
  action?: string;
  taskTableState?: TaskTableFormState;
  taskTables?: TaskTableDefinition[];
  assignNextTask?: ReturnType<typeof parseAssignNextTaskFromTaskDetail>;
  assignNextTaskFormData?: Record<string, unknown>;
  /** Present after a successful e-sign when policy gated this submit. */
  esignId?: string;
};

export interface ManualAssessmentFormProps {
  /** Task detail from GET workflow task (drives form schema + initial values) */
  taskDetail: WorkflowTaskDetail | null;
  /** Options for async/select fields keyed by field name (e.g. department from concepts) */
  optionsByField?: OptionsByField;
  /** Loading state */
  loading?: boolean;
  /** Error message */
  error?: string | null;
  /** When true, show Approve + Reject; when false, show single Submit */
  isLastStep?: boolean;
  /** Cancel button label (default: Cancel) */
  cancelButtonLabel?: string;
  /** Submit button label when not last step (default: Submit) */
  submitButtonLabel?: string;
  /** Approve button label when isLastStep (default: Approve) */
  approveButtonLabel?: string;
  /** Reject button label when isLastStep (default: Reject) */
  rejectButtonLabel?: string;
  /** Called when user clicks Cancel */
  onCancel?: () => void;
  /** Save button label (default: Save) */
  saveButtonLabel?: string;
  /** Called when user clicks Save (draft); no required-field validation */
  onSave?: (
    formData: FormData,
    context?: ManualAssessmentFormContext,
  ) => void | Promise<void>;
  /** Called when user submits; options.action is 'approve' | 'reject' | 'submit' for payload */
  onSubmit?: (
    formData: FormData,
    options?: ManualAssessmentFormContext,
  ) => void | Promise<void>;
  /** When true, disables the submit/approve/reject buttons (e.g. task already completed) */
  submitDisabled?: boolean;
  /** When true (e.g. normal path + task completed), form is read-only and buttons are hidden */
  readOnly?: boolean;
  /** When true, show the Dynamic Form (RJSF) section below (default: false) */
  showDynamicForm?: boolean;
  /** Called when Dynamic Form (RJSF) submit API succeeds */
  onDynamicFormSubmitSuccess?: () => void;
  /** Called when Dynamic Form (RJSF) submit API fails with error message */
  onDynamicFormSubmitError?: (message: string) => void;
  /** Workflow id for Reassign API (task-entry mode). */
  workflowId?: string;
  /** Task id for Reassign API (task-entry mode). */
  taskId?: string;
  /** Called after a successful Reassign. */
  onReassigned?: () => void;
  onReassignSuccess?: (message: string) => void;
  onReassignError?: (message: string) => void;
  /**
   * When set, Related Materials `previousForms` opens the PAT process in a
   * same-page side pane instead of a new browser tab.
   */
  onOpenWholeWorkflow?: (uri: string) => void;
}

const DEFAULT_CANCEL_LABEL = MANUAL_ASSESSMENT_ACTION_LABELS.cancel;
const DEFAULT_SAVE_LABEL = MANUAL_ASSESSMENT_ACTION_LABELS.save;
const DEFAULT_SUBMIT_LABEL = MANUAL_ASSESSMENT_ACTION_LABELS.submit;
const DEFAULT_APPROVE_LABEL = "Approve";
const DEFAULT_REJECT_LABEL = "Reject";
export const ManualAssessmentForm: React.FC<ManualAssessmentFormProps> = ({
  taskDetail,
  optionsByField,
  loading = false,
  error: errorProp = null,
  isLastStep = false,
  cancelButtonLabel = DEFAULT_CANCEL_LABEL,
  saveButtonLabel = DEFAULT_SAVE_LABEL,
  onSave,
  submitButtonLabel = DEFAULT_SUBMIT_LABEL,
  approveButtonLabel = DEFAULT_APPROVE_LABEL,
  rejectButtonLabel = DEFAULT_REJECT_LABEL,
  onCancel,
  onSubmit,
  submitDisabled = false,
  readOnly = false,
  showDynamicForm = true,
  onDynamicFormSubmitSuccess: _onDynamicFormSubmitSuccess,
  onDynamicFormSubmitError: _onDynamicFormSubmitError,
  workflowId,
  taskId,
  onReassigned,
  onReassignSuccess,
  onReassignError,
  onOpenWholeWorkflow,
}) => {
  const shouldRenderDynamicForm = showDynamicForm && SHOW_DYNAMIC_FORM;

  const { fields, initialValue, title } = useMemo(
    () => taskDetailToFormConfig(taskDetail, optionsByField),
    [taskDetail, optionsByField],
  );
  const dynamicFormConfig = useMemo(
    () => taskDetailToDynamicFormConfig(taskDetail, optionsByField),
    [taskDetail, optionsByField],
  );
  const dynamicFormExtraState = useMemo(
    () => buildDynamicFormExtraState(taskDetail),
    [taskDetail],
  );
  const previousTaskDynamicFormExtraState = useMemo(
    () => buildPreviousTaskDynamicFormExtraState(taskDetail),
    [taskDetail],
  );
  const assignNextTaskDefinition = useMemo(
    () => parseAssignNextTaskFromTaskDetail(taskDetail),
    [taskDetail],
  );
  const hideSaveForAssignNextTask = useMemo(
    () => hasAssignNextTaskConfig(taskDetail),
    [taskDetail],
  );
  const assignNextTaskFormConfig = useMemo(
    () => taskDetailToAssignNextTaskDynamicFormConfig(taskDetail, optionsByField),
    [taskDetail, optionsByField],
  );
  const assignNextTaskFormExtraState = useMemo(
    () => buildAssignNextTaskFormExtraState(taskDetail),
    [taskDetail],
  );
  const previousTaskReadonlyFormConfig = useMemo(
    () => taskDetailToPreviousTaskReadonlyFormConfig(taskDetail, optionsByField),
    [taskDetail, optionsByField],
  );
  const sourceFieldsReadonlySections = useMemo(
    () => taskDetailToSourceFieldsReadonlySections(taskDetail, optionsByField),
    [taskDetail, optionsByField],
  );
  const previousTaskDialogTitle = useMemo(
    () =>
      resolvePreviousTaskDialogTitle(taskDetail, VIEW_PREVIOUS_TASK_LINK_LABEL),
    [taskDetail],
  );
  const previousTaskDialogMeta = useMemo(
    () => getPreviousTaskDialogMeta(taskDetail),
    [taskDetail],
  );
  const [resolvedTaskOwnersInitial, setResolvedTaskOwnersInitial] = useState<
    Record<string, string[]> | null
  >(null);
  const [resolvedPreviousTaskOwnersInitial, setResolvedPreviousTaskOwnersInitial] =
    useState<Record<string, string[]> | null>(null);
  const [taskOwnersInitialGeneration, setTaskOwnersInitialGeneration] =
    useState(0);
  const [previousTaskReadonlyGeneration, setPreviousTaskReadonlyGeneration] =
    useState(0);

  useEffect(() => {
    let cancelled = false;
    setResolvedTaskOwnersInitial(null);
    setResolvedPreviousTaskOwnersInitial(null);
    setTaskOwnersInitialGeneration(0);
    setPreviousTaskReadonlyGeneration(0);

    const resolveOwners = (
      fieldValue: unknown,
      onResolved: (value: Record<string, string[]>) => void,
    ) => {
      void resolveTaskOwnersGroupedInitial(fieldValue).then((corrected) => {
        if (cancelled || !corrected) return;
        onResolved(corrected);
      });
    };

    const currentOwnersItem = taskDetail?.task?.propertyValueItemList?.find(
      (item) => item.field === "taskOwners",
    );
    if (currentOwnersItem?.fieldValue) {
      resolveOwners(currentOwnersItem.fieldValue, (corrected) => {
        setResolvedTaskOwnersInitial(corrected);
        setTaskOwnersInitialGeneration((g) => g + 1);
      });
    }

    const previousOwnersItem =
      resolvePreviousTaskFromTaskDetail(taskDetail)?.propertyValueItemList?.find(
        (item) => item.field === "taskOwners",
      );
    if (previousOwnersItem?.fieldValue) {
      resolveOwners(previousOwnersItem.fieldValue, (corrected) => {
        setResolvedPreviousTaskOwnersInitial(corrected);
        setPreviousTaskReadonlyGeneration((g) => g + 1);
      });
    }

    return () => {
      cancelled = true;
    };
  }, [taskDetail]);

  const dynamicInitialFormData = useMemo(
    () =>
      mergeResolvedTaskOwnersInitial(
        dynamicFormConfig.initialFormData,
        resolvedTaskOwnersInitial,
      ),
    [dynamicFormConfig.initialFormData, resolvedTaskOwnersInitial],
  );
  const taskFormIdentityKey = useMemo(() => {
    const task = taskDetail?.task;
    if (!task) return "";
    const instanceId =
      task.taskInstanceUniqueId != null
        ? String(task.taskInstanceUniqueId)
        : "";
    const taskId =
      task.taskId != null ? String(task.taskId) : "";
    const definitionId =
      task.taskDefinitionId != null ? String(task.taskDefinitionId) : "";
    return `${instanceId}:${taskId}:${definitionId}`;
  }, [taskDetail?.task]);
  const editableInitialSnapshotRef = useRef(dynamicInitialFormData);
  const prevTaskFormIdentityKeyRef = useRef(taskFormIdentityKey);
  const [isEditableFormDirty, setIsEditableFormDirty] = useState(false);
  const assignNextInitialSnapshotRef = useRef(
    assignNextTaskFormConfig.initialFormData,
  );
  const [isAssignNextFormDirty, setIsAssignNextFormDirty] = useState(false);

  useEffect(() => {
    if (prevTaskFormIdentityKeyRef.current !== taskFormIdentityKey) {
      prevTaskFormIdentityKeyRef.current = taskFormIdentityKey;
      setIsEditableFormDirty(false);
      setIsAssignNextFormDirty(false);
      editableInitialSnapshotRef.current = dynamicInitialFormData;
      assignNextInitialSnapshotRef.current =
        assignNextTaskFormConfig.initialFormData;
      return;
    }
    if (!isEditableFormDirty) {
      editableInitialSnapshotRef.current = dynamicInitialFormData;
    }
    if (!isAssignNextFormDirty) {
      assignNextInitialSnapshotRef.current =
        assignNextTaskFormConfig.initialFormData;
    }
  }, [
    taskFormIdentityKey,
    dynamicInitialFormData,
    isEditableFormDirty,
    assignNextTaskFormConfig.initialFormData,
    isAssignNextFormDirty,
  ]);

  /** While the user is editing, freeze initialFormData so post-save refetches do not reset RJSF state. */
  const editableDynamicInitialFormData = isEditableFormDirty
    ? editableInitialSnapshotRef.current
    : dynamicInitialFormData;
  const assignNextTaskInitialFormData = isAssignNextFormDirty
    ? assignNextInitialSnapshotRef.current
    : assignNextTaskFormConfig.initialFormData;
  const previousTaskReadonlyInitialFormData = useMemo(
    () =>
      mergeResolvedTaskOwnersInitial(
        previousTaskReadonlyFormConfig.initialFormData,
        resolvedPreviousTaskOwnersInitial,
      ),
    [
      previousTaskReadonlyFormConfig.initialFormData,
      resolvedPreviousTaskOwnersInitial,
    ],
  );
  const taskFormSummary = useMemo(
    () => getTaskFormSummary(taskDetail),
    [taskDetail],
  );
  const relatedMaterials = useMemo(
    () => getTaskRelatedMaterials(taskDetail),
    [taskDetail],
  );
  const relatedMaterialDisplayItems = useMemo(
    () => getTaskRelatedMaterialDisplayItems(taskDetail),
    [taskDetail],
  );

  const [openingRelatedMaterialUri, setOpeningRelatedMaterialUri] = useState<
    string | null
  >(null);
  const [relatedMaterialError, setRelatedMaterialError] = useState<string | null>(
    null,
  );
  const handleOpenRelatedMaterial = useCallback(
    async (uri: string) => {
      setRelatedMaterialError(null);
      setOpeningRelatedMaterialUri(uri);
      const material = relatedMaterials.find(
        (item) => item.uri?.trim() === uri,
      );
      const title = material ? getRelatedMaterialLabel(material) : "PDF Preview";
      try {
        if (isRelatedMaterialExternalUri(uri)) {
          if (
            material &&
            isPreviousFormsRelatedMaterial(material) &&
            onOpenWholeWorkflow
          ) {
            onOpenWholeWorkflow(uri.trim());
            return;
          }
          window.open(uri.trim(), "_blank", "noopener,noreferrer");
          return;
        }
        const { presigned_url } = await getAssessmentReportPresignedUrl(uri);
        await openPdfUrlInNewTabForPreview(presigned_url, title);
      } catch (err) {
        console.error("[ManualAssessmentForm] related material open failed", err);
        setRelatedMaterialError(
          err instanceof Error ? err.message : "Failed to open document",
        );
      } finally {
        setOpeningRelatedMaterialUri(null);
      }
    },
    [relatedMaterials, onOpenWholeWorkflow],
  );

  const freyrLibraryContext = useContext(FreyrLibraryContext);
  const theme = useTheme();
  /** Raise meta-row tooltips (Available Owners) above the elevated dialog. */
  const previousTaskDialogTheme = useMemo(
    () =>
      createTheme(theme, {
        zIndex: {
          tooltip:
            theme.zIndex.modal +
            ELEVATED_DIALOG_Z_INDEX_OFFSET +
            ELEVATED_DIALOG_TOOLTIP_Z_INDEX_OFFSET,
        },
      }),
    [theme],
  );

  const [formData, setFormData] = useState<FormData>(() => ({
    ...initialValue,
  }));
  const [submitErrors, setSubmitErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dynamicFormRef = useRef<DynamicFormRef>(null);
  const assignNextTaskFormRef = useRef<DynamicFormRef>(null);
  const pendingDynamicSubmitActionRef = useRef<DynamicFormSubmitAction>("submit");
  const actionInFlightRef = useRef(false);
  const hasDynamicFields = dynamicFormConfig.jsonData.length > 0;
  const hasAssignNextTaskFields = assignNextTaskFormConfig.jsonData.length > 0;
  const hasPreviousTaskReadonlyFields =
    previousTaskReadonlyFormConfig.jsonData.length > 0;
  const hasSourceFieldsReadonlySections =
    sourceFieldsReadonlySections.length > 0;
  const previousTaskDisplayMode = resolvePreviousTaskDisplayMode(taskDetail);
  // Link whenever previous task has displayable fields; mode only gates inline form.
  const showViewPreviousTaskLink = hasPreviousTaskReadonlyFields;
  const showPreviousTaskInline =
    previousTaskDisplayMode === "inline" && hasPreviousTaskReadonlyFields;
  const [previousTaskDialogOpen, setPreviousTaskDialogOpen] = useState(false);
  const [isESignOpen, setIsESignOpen] = useState(false);
  const [eSignResolved, setESignResolved] = useState<ResolvedESignAction | null>(
    null,
  );
  /** Lazily loaded so @freyr/freyr-react-common-components is not in the boot bundle. */
  const [eSignModule, setESignModule] = useState<ESignModule | null>(null);
  const [eSignLoadError, setESignLoadError] = useState<string | null>(null);
  const pendingESignSubmitRef = useRef<{
    formData: FormData;
    context: ManualAssessmentFormContext;
  } | null>(null);

  const closeESignPopup = useCallback(() => {
    setIsESignOpen(false);
    setESignResolved(null);
    pendingESignSubmitRef.current = null;
    actionInFlightRef.current = false;
    setIsSubmitting(false);
  }, []);

  const loadESignModule = useCallback(async () => {
    if (eSignModule) return eSignModule;
    const mod = await import("@freyr/freyr-react-common-components");
    const loaded: ESignModule = {
      ESignValidation: mod.ESignValidation,
      FreyrComponentLibraryProvider: mod.FreyrComponentLibraryProvider,
    };
    setESignModule(loaded);
    return loaded;
  }, [eSignModule]);

  const submitOrRequestESign = useCallback(
    async (
      data: FormData,
      context: ManualAssessmentFormContext = {},
    ): Promise<"submitted" | "esign-pending"> => {
      const resolved = resolveESignActionForSubmit({
        taskDetail,
        formData: data,
        action: context.action,
        taskTables: context.taskTables,
        taskTableState: context.taskTableState,
      });
      if (!resolved) {
        await Promise.resolve(onSubmit?.(data, context));
        return "submitted";
      }
      pendingESignSubmitRef.current = { formData: data, context };
      setESignResolved(resolved);
      setESignLoadError(null);
      try {
        await loadESignModule();
        setIsESignOpen(true);
      } catch (err) {
        setESignLoadError(
          err instanceof Error
            ? err.message
            : "Failed to load e-signature module",
        );
        pendingESignSubmitRef.current = null;
        setESignResolved(null);
        actionInFlightRef.current = false;
        setIsSubmitting(false);
        throw err;
      }
      return "esign-pending";
    },
    [loadESignModule, onSubmit, taskDetail],
  );

  const handleESignSuccess = useCallback(
    (result?: ESignCallbackPayload) => {
      const pending = pendingESignSubmitRef.current;
      pendingESignSubmitRef.current = null;
      setIsESignOpen(false);
      setESignResolved(null);
      if (!pending) {
        actionInFlightRef.current = false;
        setIsSubmitting(false);
        return;
      }
      const submitContext: ManualAssessmentFormContext = {
        ...pending.context,
        esignId: result?.esignId,
      };
      void (async () => {
        try {
          await Promise.resolve(onSubmit?.(pending.formData, submitContext));
        } finally {
          actionInFlightRef.current = false;
          setIsSubmitting(false);
        }
      })();
    },
    [onSubmit],
  );

  const handleESignError = useCallback(() => {
    closeESignPopup();
  }, [closeESignPopup]);

  const handleESignCancel = useCallback(() => {
    closeESignPopup();
  }, [closeESignPopup]);

  const taskTables = useMemo(
    () => parseTaskTablesFromTaskDetail(taskDetail),
    [taskDetail],
  );

  const hasAnyDynamicFields =
    hasDynamicFields ||
    showPreviousTaskInline ||
    hasAssignNextTaskFields ||
    hasPreviousTaskReadonlyFields ||
    hasSourceFieldsReadonlySections ||
    taskTables.length > 0;

  const [taskTableState, setTaskTableState] = useState<TaskTableFormState>({});
  const [taskTableValidationErrors, setTaskTableValidationErrors] = useState<
    TaskTableCellValidationError[]
  >([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignSubmitting, setReassignSubmitting] = useState(false);
  const [dynamicFormAsyncState, setDynamicFormAsyncState] = useState<
    Record<string, unknown>
  >({});

  useEffect(() => {
    setDynamicFormAsyncState(dynamicInitialFormData);
  }, [dynamicInitialFormData]);

  // Fallback for table async selects. Per-cell overlay uses nested-task owners
  // (Assign To SME / Reviewer) from taskTables[].tasks when present.
  const taskTableAsyncFormData = useMemo((): FormData => {
    const taskOwnersFromDetail = taskDetail?.task?.propertyValueItemList?.find(
      (item) => item.field === "taskOwners",
    )?.fieldValue;
    const taskOwners =
      dynamicFormAsyncState.taskOwners ??
      resolvedTaskOwnersInitial ??
      taskOwnersFromDetail;

    return {
      ...(dynamicInitialFormData as FormData),
      ...dynamicFormAsyncState,
      ...(taskOwners !== undefined ? { taskOwners } : {}),
    } as FormData;
  }, [
    dynamicFormAsyncState,
    dynamicInitialFormData,
    resolvedTaskOwnersInitial,
    taskDetail,
  ]);

  useEffect(() => {
    setTaskTableState(buildInitialTaskTableState(taskTables));
    setTaskTableValidationErrors([]);
  }, [taskDetail, taskTables]);

  const handleTaskTableChange = useCallback((nextState: TaskTableFormState) => {
    setTaskTableState(nextState);
    setTaskTableValidationErrors([]);
  }, []);

  const validateTaskTablesBeforeSubmit = useCallback((): boolean => {
    const errors = validateTriageTaskTables(taskTables, taskTableState);
    setTaskTableValidationErrors(errors);
    return errors.length === 0;
  }, [taskTables, taskTableState]);

  /** Block submit when required; only surface Dynamic Form field-level required UI. */
  const validateAssignNextTaskBeforeSubmit = useCallback((): boolean => {
    if (!hasAssignNextTaskFields) return true;
    const assignNextTaskFormData =
      assignNextTaskFormRef.current?.getFormState() ?? {};
    const validationError = validateAssignNextTaskFormData(
      assignNextTaskFormConfig.jsonData,
      assignNextTaskFormData,
    );
    if (validationError) {
      assignNextTaskFormRef.current?.submitForm();
      return false;
    }
    return true;
  }, [assignNextTaskFormConfig.jsonData, hasAssignNextTaskFields]);

  const taskTableContext = useMemo(
    (): ManualAssessmentFormContext => ({
      taskTableState,
      taskTables,
      assignNextTask: assignNextTaskDefinition ?? undefined,
      assignNextTaskFormData:
        assignNextTaskFormRef.current?.getFormState() ?? undefined,
    }),
    [taskTableState, taskTables, assignNextTaskDefinition],
  );

  const buildAssignNextTaskContext = useCallback(
    (): Pick<
      ManualAssessmentFormContext,
      "assignNextTask" | "assignNextTaskFormData"
    > => {
      const assignNextTaskFormData =
        assignNextTaskFormRef.current?.getFormState() ?? {};
      return {
        assignNextTask: buildAssignNextTaskSubmitValue(
          assignNextTaskDefinition,
          assignNextTaskFormData,
        ),
        assignNextTaskFormData,
      };
    },
    [assignNextTaskDefinition],
  );

  const showReassignButton =
    Boolean(workflowId && taskId) &&
    isTaskReassignable(taskDetail?.task?.propertyValueItemList) &&
    !readOnly &&
    !submitDisabled;
  const legacyCancelLabel = formatActionButtonLabel(
    cancelButtonLabel,
    MANUAL_ASSESSMENT_ACTION_LABELS.cancel,
  );
  const legacySaveLabel = formatActionButtonLabel(
    saveButtonLabel,
    MANUAL_ASSESSMENT_ACTION_LABELS.save,
  );
  const legacySubmitLabel = formatActionButtonLabel(
    submitButtonLabel,
    MANUAL_ASSESSMENT_ACTION_LABELS.submit,
  );

  const handleDynamicFormPreviewSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      if (actionInFlightRef.current) return;
      const action = pendingDynamicSubmitActionRef.current;
      pendingDynamicSubmitActionRef.current = "submit";
      actionInFlightRef.current = true;
      setIsSubmitting(true);
      try {
        const assignNextContext = buildAssignNextTaskContext();
        if (hasAssignNextTaskFields) {
          const validationError = validateAssignNextTaskFormData(
            assignNextTaskFormConfig.jsonData,
            assignNextContext.assignNextTaskFormData ?? {},
          );
          if (validationError) {
            assignNextTaskFormRef.current?.submitForm();
            return;
          }
        }
        const gate = await submitOrRequestESign(data as FormData, {
          ...taskTableContext,
          ...assignNextContext,
          action,
        });
        if (gate === "esign-pending") {
          // Keep busy flags until e-sign success / cancel / error.
          return;
        }
      } finally {
        if (!pendingESignSubmitRef.current) {
          actionInFlightRef.current = false;
          setIsSubmitting(false);
        }
      }
    },
    [
      assignNextTaskFormConfig.jsonData,
      buildAssignNextTaskContext,
      hasAssignNextTaskFields,
      submitOrRequestESign,
      taskTableContext,
    ],
  );

  const handleDynamicFormChange = useCallback(() => {
    setIsEditableFormDirty(true);
    const latest = dynamicFormRef.current?.getFormState();
    if (latest) {
      setDynamicFormAsyncState(latest);
    }
  }, []);

  const handleAssignNextTaskFormChange = useCallback(() => {
    setIsAssignNextFormDirty(true);
  }, []);

  const handleDynamicFormKeyDownCapture = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      allowTextareaEnterNewlineCapture(e);
    },
    [],
  );

  const triggerDynamicFormSubmit = useCallback((action: DynamicFormSubmitAction) => {
    pendingDynamicSubmitActionRef.current = action;
    if (hasDynamicFields) {
      dynamicFormRef.current?.submitForm();
      return;
    }
    if (hasAssignNextTaskFields) {
      void handleDynamicFormPreviewSubmit({});
    }
  }, [handleDynamicFormPreviewSubmit, hasAssignNextTaskFields, hasDynamicFields]);

  const handleDynamicFormSave = useCallback(async () => {
    if (!onSave || actionInFlightRef.current) return;
    const formState = dynamicFormRef.current?.getFormState() ?? {};
    const assignNextContext = buildAssignNextTaskContext();
    actionInFlightRef.current = true;
    setIsSaving(true);
    try {
      await onSave(
        formState as FormData,
        {
          ...taskTableContext,
          ...assignNextContext,
        },
      );
      const latestState = dynamicFormRef.current?.getFormState() ?? formState;
      editableInitialSnapshotRef.current = latestState;
      setIsEditableFormDirty(false);
      const latestAssignNext =
        assignNextTaskFormRef.current?.getFormState() ??
        assignNextContext.assignNextTaskFormData ??
        {};
      assignNextInitialSnapshotRef.current = latestAssignNext;
      setIsAssignNextFormDirty(false);
    } catch {
      // Keep dirty state so a retry still uses live form values.
    } finally {
      actionInFlightRef.current = false;
      setIsSaving(false);
    }
  }, [onSave, taskTableContext, buildAssignNextTaskContext]);

  const handleLegacyFormSave = useCallback(async () => {
    if (!onSave) return;
    try {
      await onSave(formData);
    } catch {
      // Parent shows error snackbar; keep current formData for retry.
    }
  }, [formData, onSave]);

  const handleDynamicSubmit = useCallback(() => {
    if (!validateTaskTablesBeforeSubmit()) return;
    if (!validateAssignNextTaskBeforeSubmit()) return;
    triggerDynamicFormSubmit("submit");
  }, [
    triggerDynamicFormSubmit,
    validateAssignNextTaskBeforeSubmit,
    validateTaskTablesBeforeSubmit,
  ]);
  const handleDynamicApprove = useCallback(() => {
    if (!validateTaskTablesBeforeSubmit()) return;
    if (!validateAssignNextTaskBeforeSubmit()) return;
    triggerDynamicFormSubmit("approve");
  }, [
    triggerDynamicFormSubmit,
    validateAssignNextTaskBeforeSubmit,
    validateTaskTablesBeforeSubmit,
  ]);
  const handleDynamicReject = useCallback(() => {
    if (!validateTaskTablesBeforeSubmit()) return;
    if (!validateAssignNextTaskBeforeSubmit()) return;
    triggerDynamicFormSubmit("reject");
  }, [
    triggerDynamicFormSubmit,
    validateAssignNextTaskBeforeSubmit,
    validateTaskTablesBeforeSubmit,
  ]);

  const handleReassignConfirm = useCallback(
    async (userId: string) => {
      if (!workflowId || !taskId) return;
      setReassignSubmitting(true);
      try {
        const res = await reassignWorkflowTask(workflowId, taskId, {
          newAssignee: { userId },
        });
        if (res.status !== "success") {
          throw new Error(res.message ?? "Reassign failed");
        }
        onReassignSuccess?.("Task reassigned successfully.");
        setReassignOpen(false);
        onReassigned?.();
      } catch (err) {
        onReassignError?.(
          err instanceof Error ? err.message : "Reassign failed",
        );
      } finally {
        setReassignSubmitting(false);
      }
    },
    [
      workflowId,
      taskId,
      onReassigned,
      onReassignSuccess,
      onReassignError,
    ],
  );

  const handleChange = useCallback((data: FormData) => {
    setFormData(data);
    setSubmitErrors({});
    setIsEditableFormDirty(true);
  }, []);

  const submitWithAction = useCallback(
    (action: string) => {
      const errs = validateFormData(fields, formData);
      if (Object.keys(errs).length > 0) {
        setSubmitErrors(errs);
        return;
      }
      setSubmitErrors({});
      void submitOrRequestESign(formData, { action });
    },
    [fields, formData, submitOrRequestESign],
  );

  const submitWithActionRef = useRef(submitWithAction);
  submitWithActionRef.current = submitWithAction;

  const fireAction = useCallback((action: string) => {
    submitWithActionRef.current(action);
  }, []);
  const debouncedFireAction = useDebounce<[string]>(fireAction, DEBOUNCE_MS);

  const handleSubmit = useCallback(
    () => debouncedFireAction("submit"),
    [debouncedFireAction],
  );
  const handleApprove = useCallback(
    () => debouncedFireAction("approve"),
    [debouncedFireAction],
  );
  const handleReject = useCallback(
    () => debouncedFireAction("reject"),
    [debouncedFireAction],
  );

  if (loading) {
    return (
      <Box
        id="manual-assessment-form"
        sx={{
          width: "100%",
          py: 4,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const hasFields = fields.length > 0;
  const isActionBusy = isSaving || isSubmitting;
  const actionButtonLoadingIcon = (
    <CircularProgress size={ACTION_BUTTON_LOADING_ICON_SIZE} color="inherit" />
  );

  return (
    <Box id="manual-assessment-form" sx={{ width: "100%" }}>
      {errorProp && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorProp}
        </Alert>
      )}
      <Card
        variant="elevation"
        elevation={0}
        sx={{
          borderRadius: 0,
          overflow: "hidden",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {title ?? "Task"}
        </Typography>
        {taskFormSummary ? (
          <TaskFormSummaryBar
            taskDetail={taskDetail}
            summary={taskFormSummary}
            title={title}
          />
        ) : null}
        {showReassignButton ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
            <Button variant="outlined" onClick={() => setReassignOpen(true)}>
              Reassign
            </Button>
          </Box>
        ) : null}
     
        <Box sx={{ pb: 2, borderBottom: "1px solid #F2F2F2" }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1.5,
            pt: 1.5,
            pb: 2,
            mb: 2,
          }}
        >
          <TaskFormStepIndicator stepNumber={1} label="Review Materials" />
          <Box component="span" sx={TASK_STEP_CONNECTOR_SX} aria-hidden />
          <TaskFormStepIndicator stepNumber={2} label="Complete the Form" />
        </Box>
        <TaskRelatedMaterialsSection
          displayItems={relatedMaterialDisplayItems}
          openingUri={openingRelatedMaterialUri}
          error={relatedMaterialError}
          onOpen={handleOpenRelatedMaterial}
          showViewPreviousTaskLink={showViewPreviousTaskLink}
          onOpenPreviousTask={() => setPreviousTaskDialogOpen(true)}
        />
        <CardContent sx={{ p: 0 }}>
          {shouldRenderDynamicForm ? (
            hasAnyDynamicFields ? (
              <Box onKeyDownCapture={handleDynamicFormKeyDownCapture}>
                <NotistackProvider
                  maxSnack={3}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                <CommonFreyrLibraryProvider {...(freyrLibraryContext ?? {})}>
                  {hasSourceFieldsReadonlySections ? (
                    <>
                      {sourceFieldsReadonlySections.map((section, index) => (
                        <Box
                          key={`source-fields-${section.sourceTaskName || index}`}
                          sx={{ mb: index === sourceFieldsReadonlySections.length - 1 ? 0 : 2 }}
                        >
                          <Box
                            data-testid="source-fields-readonly"
                            sx={SOURCE_FIELDS_READONLY_SECTION_SX}
                          >
                            <CommonDynamicFormCompBasicJsonSchema
                              uniqueKey={`manual-assessment-dynamic-form-source-${index}`}
                              jsonData={section.config.jsonData}
                              initialFormData={section.config.initialFormData}
                              extraState={section.extraState}
                              removeButtons
                              viewType
                            />
                          </Box>
                        </Box>
                      ))}
                      {(showPreviousTaskInline ||
                        hasDynamicFields ||
                        hasAssignNextTaskFields ||
                        taskTables.length > 0) && (
                        <Box sx={MANUAL_ASSESSMENT_SECTION_DIVIDER_SX} aria-hidden />
                      )}
                    </>
                  ) : null}
                  {showPreviousTaskInline ? (
                    <>
                      {/*
                        Prior-step summary: no viewType / form-level disabled so fields use
                        CustomSelectWidget / CustomSelectGroupWidget and fetch asyncConfig
                        (e.g. global_filter_search for department) like the editable form below.
                        Field-level readonly/disabled comes from jsonData (readonlyPrevious mode).
                      */}
                      <Box sx={DYNAMIC_FORM_READONLY_SECTION_SX}>
                        <CommonDynamicFormCompBasicJsonSchema
                          uniqueKey={`manual-assessment-dynamic-form-previous-${previousTaskReadonlyGeneration}`}
                          jsonData={previousTaskReadonlyFormConfig.jsonData}
                          initialFormData={previousTaskReadonlyInitialFormData}
                          extraState={previousTaskDynamicFormExtraState}
                          removeButtons
                          viewType
                        />
                      </Box>
                      {hasDynamicFields && (
                        <Box sx={MANUAL_ASSESSMENT_SECTION_DIVIDER_SX} aria-hidden />
                      )}
                    </>
                  ) : null}
                   {taskTables.length > 0 ? (
                      <ManualAssessmentTaskTable
                        tables={taskTables}
                        state={taskTableState}
                        onChange={handleTaskTableChange}
                        readOnly={readOnly || submitDisabled}
                        asyncFormData={taskTableAsyncFormData}
                        validationErrors={taskTableValidationErrors}
                      />
                  ) : null}
                  {hasDynamicFields && (
                    <>
                      {/*
                        initialFormData.department / .taskResult: taskDetailToDynamicFormConfig (explicit keys; do not mix assessment_required with taskResult).
                        @freyr/freyr-react-common@1.0.0-ptqa.6.1 always sets RJSF omitExtraData internally (no prop).
                      */}
                      <Box sx={DYNAMIC_FORM_EDITABLE_SECTION_SX}>
                        <CommonDynamicFormCompBasicJsonSchema
                          ref={dynamicFormRef}
                          uniqueKey={`manual-assessment-dynamic-form-common-${taskOwnersInitialGeneration}`}
                          jsonData={dynamicFormConfig.jsonData}
                          initialFormData={editableDynamicInitialFormData}
                          extraState={dynamicFormExtraState}
                          onFormSubmit={handleDynamicFormPreviewSubmit}
                          onChange={handleDynamicFormChange}
                          removeButtons
                          disabled={readOnly || submitDisabled}
                          viewType={readOnly}
                          checkDownloadable={true}
                        />
                      </Box>
                    </>
                  )}
                  {hasAssignNextTaskFields && (
                    <>
                      <Box sx={MANUAL_ASSESSMENT_SECTION_DIVIDER_SX} aria-hidden />
                      <Box sx={DYNAMIC_FORM_EDITABLE_SECTION_SX}>
                        <CommonDynamicFormCompBasicJsonSchema
                          ref={assignNextTaskFormRef}
                          uniqueKey={`manual-assessment-assign-next-${taskFormIdentityKey || "default"}`}
                          jsonData={assignNextTaskFormConfig.jsonData}
                          initialFormData={assignNextTaskInitialFormData}
                          extraState={assignNextTaskFormExtraState}
                          onChange={handleAssignNextTaskFormChange}
                          removeButtons
                          disabled={readOnly || submitDisabled}
                          viewType={readOnly}
                        />
                      </Box>
                    </>
                  )}

                </CommonFreyrLibraryProvider>
                </NotistackProvider>
                {!readOnly && (hasDynamicFields || hasAssignNextTaskFields) && (
                  <Box sx={FORM_FOOTER_SX}>
                    <Button variant="outlined" onClick={onCancel}>
                      {MANUAL_ASSESSMENT_ACTION_LABELS.cancel}
                    </Button>
                    {onSave && !hideSaveForAssignNextTask && (
                      <Button
                        variant="outlined"
                        onClick={() => void handleDynamicFormSave()}
                        disabled={submitDisabled || isActionBusy}
                        startIcon={isSaving ? actionButtonLoadingIcon : undefined}
                      >
                        {MANUAL_ASSESSMENT_ACTION_LABELS.save}
                      </Button>
                    )}
                    {isLastStep ? (
                      <>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleDynamicReject}
                          disabled={submitDisabled || isActionBusy}
                        >
                          {rejectButtonLabel}
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleDynamicApprove}
                          disabled={submitDisabled || isActionBusy}
                          startIcon={isSubmitting ? actionButtonLoadingIcon : undefined}
                          sx={PRIMARY_ACTION_BUTTON_SX}
                        >
                          {approveButtonLabel}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDynamicSubmit}
                        disabled={submitDisabled || isActionBusy}
                        startIcon={isSubmitting ? actionButtonLoadingIcon : undefined}
                        sx={PRIMARY_ACTION_BUTTON_SX}
                      >
                        {MANUAL_ASSESSMENT_ACTION_LABELS.submit}
                      </Button>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="info">
                No dynamic form fields defined for this task.
              </Alert>
            )
          ) : hasFields && SHOW_LEGACY_FORM ? (
            <>
              <FormRenderer
                fields={fields}
                value={formData}
                onChange={handleChange}
                showSaveButton={false}
                errors={submitErrors}
                disabled={readOnly}
                paperSx={{ border: "none", boxShadow: "none", p: 0 }}
              />
              {!readOnly && (
                <Box sx={FORM_FOOTER_SX}>
                  <Button variant="outlined" onClick={onCancel}>
                    {legacyCancelLabel}
                  </Button>
                  {onSave && !hideSaveForAssignNextTask && (
                    <Button
                      variant="outlined"
                      onClick={() => void handleLegacyFormSave()}
                      disabled={submitDisabled}
                    >
                      {legacySaveLabel}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    sx={PRIMARY_ACTION_BUTTON_SX}
                  >
                    {legacySubmitLabel}
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="info">
              No dynamic form fields defined for this task.
            </Alert>
          )}
        </CardContent>
      </Card>
      <ManualAssessmentReassignDialog
        open={reassignOpen}
        submitting={reassignSubmitting}
        onClose={() => setReassignOpen(false)}
        onConfirm={handleReassignConfirm}
      />
      <ThemeProvider theme={previousTaskDialogTheme}>
        {previousTaskDialogOpen ? (
          <GlobalStyles styles={PREVIOUS_TASK_HIDE_CONTENT_TOOLTIPS_SX} />
        ) : null}
        <Dialog
          open={previousTaskDialogOpen}
          onClose={withoutBackdropClose(() => setPreviousTaskDialogOpen(false))}
          maxWidth={false}
          slotProps={{ paper: { sx: PREVIOUS_TASK_DIALOG_PAPER_SX } }}
          aria-labelledby="previous-task-dialog-title"
          sx={{ zIndex: (theme) => theme.zIndex.modal + ELEVATED_DIALOG_Z_INDEX_OFFSET }}
        >
          <DialogTitle
            id="previous-task-dialog-title"
            sx={PREVIOUS_TASK_DIALOG_TITLE_SX}
          >
            <Box component="span" sx={{ minWidth: 0, flex: 1, pr: 1 }}>
              {previousTaskDialogTitle}
            </Box>
            <IconButton
              aria-label="Close previous task"
              onClick={() => setPreviousTaskDialogOpen(false)}
              sx={DIALOG_CLOSE_BUTTON_SX}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 3, pt: 0, pb: 1 }}>
            {previousTaskDialogMeta ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  columnGap: 3,
                  rowGap: 0.5,
                  pb: 2,
                  mb: 2,
                  borderBottom: "1px solid #F2F2F2",
                }}
              >
                {previousTaskDialogMeta.availableOwnerCount > 0 ? (
                  <PreviousTaskDialogMetaField
                    label="Available Owners"
                    value={`${previousTaskDialogMeta.availableOwnerCount} ${
                      previousTaskDialogMeta.availableOwnerCount === 1
                        ? "person"
                        : "people"
                    }`}
                    tooltip={previousTaskDialogMeta.availableOwnerNames}
                  />
                ) : null}
                {previousTaskDialogMeta.assigneeName ? (
                  <PreviousTaskDialogMetaField
                    label="Assignee"
                    value={previousTaskDialogMeta.assigneeName}
                  />
                ) : null}
                {previousTaskDialogMeta.taskStartDate ? (
                  <PreviousTaskDialogMetaField
                    label="Task Start Date"
                    value={previousTaskDialogMeta.taskStartDate}
                  />
                ) : null}
                {previousTaskDialogMeta.taskEndDate ? (
                  <PreviousTaskDialogMetaField
                    label="Task End Date"
                    value={previousTaskDialogMeta.taskEndDate}
                  />
                ) : null}
              </Box>
            ) : null}
            {hasPreviousTaskReadonlyFields ? (
              <NotistackProvider
                maxSnack={3}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
              >
                <CommonFreyrLibraryProvider {...(freyrLibraryContext ?? {})}>
                  <Box sx={PREVIOUS_TASK_DIALOG_CONTENT_FORM_SX}>
                    <CommonDynamicFormCompBasicJsonSchema
                      uniqueKey={`manual-assessment-dynamic-form-previous-modal-${previousTaskReadonlyGeneration}`}
                      jsonData={previousTaskReadonlyFormConfig.jsonData}
                      initialFormData={previousTaskReadonlyInitialFormData}
                      extraState={previousTaskDynamicFormExtraState}
                      removeButtons
                      viewType
                    />
                  </Box>
                </CommonFreyrLibraryProvider>
              </NotistackProvider>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setPreviousTaskDialogOpen(false)}
              sx={PREVIOUS_TASK_OK_BUTTON_SX}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </ThemeProvider>
      {eSignLoadError ? (
        <Typography sx={{ mt: 1, fontSize: 12, color: "error.main" }}>
          {eSignLoadError}
        </Typography>
      ) : null}
      {isESignOpen && eSignResolved && eSignModule
        ? (() => {
            const {
              FreyrComponentLibraryProvider: ESignProvider,
              ESignValidation: ESignDialog,
            } = eSignModule;
            return (
              <I18nextProvider i18n={esignI18n}>
                <ESignProvider
                  language={ESIGN_LANGUAGE}
                  token={getAuthorizationHeader()}
                  tenant={getTenantId()}
                  domain={getDomain()}
                  domainName={getDomain()}
                  useEmail={getUserId()}
                  moduleName={ESIGN_MODULE_NAME}
                  envVars={{
                    IM_PREFIX: getApiBaseUrl().replace(/\/$/, ""),
                    URF_PREFIX: getHostApiBaseUrl().replace(/\/$/, ""),
                  }}
                >
                  <ESignDialog
                    isOpenEsigntrue={isESignOpen}
                    onCancel={handleESignCancel}
                    handleEsignatureSuccess={handleESignSuccess}
                    handleEsignatureError={handleESignError}
                    meaning={eSignResolved.meaning}
                    reason={eSignResolved.reason}
                    progressStateButtonsignText={ESIGN_DIALOG_TITLE_SUFFIX}
                  />
                </ESignProvider>
              </I18nextProvider>
            );
          })()
        : null}
    </Box>
  );
};
